import React, { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";
import { Link } from "react-router-dom";
import { AlertCircle, BriefcaseBusiness, Loader2 } from "lucide-react";

const BACKEND =
  process.env.REACT_APP_BACKEND_URL || "https://jobzam-backend.onrender.com";

function formatSalary(min, max) {
  if (!min && !max) return "Salary not listed";
  if (min && max) return `£${min.toLocaleString()} - £${max.toLocaleString()}`;
  if (min) return `From £${min.toLocaleString()}`;
  return `Up to £${max.toLocaleString()}`;
}

function formatAppliedDate(isoString) {
  if (!isoString) return null;
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return null;

  // Nice, simple UK-friendly display
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function AppliedJobsPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [unapplyingId, setUnapplyingId] = useState(null);

  const getSessionOrThrow = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error("Please login to view applied jobs");
    return session;
  };

  const fetchApplied = async () => {
    try {
      setErr("");
      setLoading(true);

      const session = await getSessionOrThrow();

      const res = await fetch(`${BACKEND}/jobs/applied`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
          `Failed to load applied jobs (${res.status})${text ? `: ${text}` : ""}`
        );
      }

      const data = await res.json();

      // Support both shapes: { jobs: [...] } OR [...]
      const list = Array.isArray(data) ? data : data.jobs || [];
      setJobs(list);
    } catch (e) {
      setErr(e.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const unmarkApplied = async (jobId) => {
    try {
      setUnapplyingId(jobId);

      const session = await getSessionOrThrow();

      const res = await fetch(`${BACKEND}/jobs/action`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ job_id: jobId, applied: false }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
          `Failed to unmark applied (${res.status})${text ? `: ${text}` : ""}`
        );
      }

      // Fast UI update
      setJobs((prev) => prev.filter((j) => j.id !== jobId));
    } catch (e) {
      alert(e.message || "Failed to unmark applied");
    } finally {
      setUnapplyingId(null);
    }
  };

  useEffect(() => {
    fetchApplied();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div role="status" className="flex min-h-52 flex-col items-center justify-center rounded-3xl bg-slate-50 text-center text-slate-600"><Loader2 className="mb-3 h-7 w-7 animate-spin text-blue-600" aria-hidden="true" /><p className="font-semibold">Loading applied jobs...</p></div>;
  if (err) return <div role="alert" className="rounded-3xl border border-rose-200 bg-rose-50 p-7 text-center"><AlertCircle className="mx-auto h-7 w-7 text-rose-600" aria-hidden="true" /><p className="mt-3 font-semibold text-rose-800">We couldn’t load your applied jobs.</p><button type="button" onClick={fetchApplied} className="mt-4 rounded-xl bg-white px-4 py-2 text-sm font-bold text-rose-700 shadow-sm ring-1 ring-rose-200">Try again</button></div>;

  if (!jobs.length) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-slate-50 p-8 text-center">
        <BriefcaseBusiness className="mx-auto h-8 w-8 text-blue-600" aria-hidden="true" />
        <h2 className="mt-4 text-xl font-bold text-slate-950">No applied jobs yet</h2>
        <p className="mt-2 text-slate-600">Track roles here after you mark them as applied.</p>
        <Link className="mt-5 inline-flex min-h-11 items-center justify-center rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white" to="/jobs">Explore jobs</Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Application Tracker</h2>
          <p className="text-sm text-slate-600">
            {jobs.length} applied job{jobs.length === 1 ? "" : "s"} tracked
          </p>
        </div>

        <button
          onClick={fetchApplied}
          className="w-full sm:w-auto px-4 py-2 rounded-lg bg-slate-100 text-slate-800 font-semibold hover:bg-slate-200 transition text-sm"
        >
          Refresh
        </button>
      </div>

      {/* List */}
      <div className="grid gap-4">
        {jobs.map((job) => {
          const appliedOn = formatAppliedDate(job?._action?.action_created_at);
          const hasUrl = !!(job.job_url && String(job.job_url).trim());

          return (
            <div
              key={job.id}
              className="border border-slate-200 rounded-2xl p-5 bg-white hover:shadow-md transition"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <Link to={`/jobs/${job.id}`}>
                    <h3 className="text-lg font-semibold text-slate-900 hover:text-indigo-700 truncate">
                      {job.title}
                    </h3>
                  </Link>

                  <p className="text-sm text-slate-600 mt-1">
                    <span className="font-medium">{job.company}</span> • {job.location} •{" "}
                    {job.country}
                  </p>

                  <p className="text-sm text-slate-600 mt-1">
                    {formatSalary(job.salary_min, job.salary_max)}
                  </p>

                  <div className="mt-2 flex flex-wrap gap-2 items-center">
                    <span className="px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-semibold">
                      Applied
                    </span>

                    {appliedOn ? (
                      <span className="text-xs text-slate-500">
                        Applied on <span className="font-semibold">{appliedOn}</span>
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2 shrink-0">
                  {hasUrl ? (
                    <a
                      href={job.job_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                    >
                      Open →
                    </a>
                  ) : (
                    <span className="text-xs text-slate-400">No link</span>
                  )}

                  <button
                    onClick={() => unmarkApplied(job.id)}
                    disabled={unapplyingId === job.id}
                    className={
                      "px-3 py-1 rounded-md text-xs transition " +
                      (unapplyingId === job.id
                        ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200")
                    }
                  >
                    {unapplyingId === job.id ? "..." : "Unmark"}
                  </button>
                </div>
              </div>

              {job.description?.trim() ? (
                <p className="text-sm text-slate-700 mt-3 line-clamp-3">
                  {job.description}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
